import { getCurrentDateTime } from "@common/components/helpers/datetime";

import SelectComponent from "@common/components/shared/forms/SelectComponent";
import InputComponent from "@common/components/shared/forms/InputComponent";
import DateComponent from "@common/components/shared/forms/DateComponent";
import TextareaComponent from "@common/components/shared/forms/TextareaComponent";
import PhotoComponent from "@common/components/shared/forms/PhotoComponent";

export const transformOptions = (items) => {
  return items.map((item) => ({
    key: item.id,
    value: { id: item.id, name: item.name },
    text: item.name,
  }));
};

/**
 * @typedef {Object} IdentityItem
 * @property {string} name - The unique name for the field.
 * @property {string} label - The display label for the field.
 * @property {string} placeholder - The display placeholder for the field.
 * @property {Option} options - The options associated with the select component.
 * @property {Object} [error=null] - Object containing validation error.
 */

/**
 * An array of items for the select component.
 *
 * @returns {IdentityItem[]} The list of items for the identity form's select component
 */
export const mapIdentityOptions = (data, optionalName) => {
  if (!data) {
    console.error("Identity form options data is empty");
    return [];
  }

  const ageRanges = transformOptions(data.ageRanges);
  const builds = transformOptions(data.builds);
  const ethnicities = transformOptions(data.ethnicity);
  const genders = transformOptions(data.genders);
  const hairs = transformOptions(data.hair);
  const heights = transformOptions(data.heights);

  const {
    hasGender,
    hasPhoneNumber,
    hasEmailAddress,
    hasAgeRange,
    hasDateOfBirth,
    hasHeight,
    hasBuild,
    hasHair,
    hasEthnicity,
    hasPhoto,
  } = data.customerConfiguration ?? {
    hasPhoneNumber: 0,
    hasGender: 0,
    hasEmailAddress: 0,
    hasAgeRange: 0,
    hasDateOfBirth: 0,
    hasHeight: 0,
    hasBuild: 0,
    hasHair: 0,
    hasEthnicity: 0,
    hasPhoto: 0,
  };

  const items = [];

  if (hasPhoto) {
    items.push({
      name: "photo",
      label: "Photo",
      photoSize: 150,
      component: PhotoComponent,
    });
  }

  if (!optionalName) {
    items.push({
      name: "forename",
      label: "First Name",
      placeholder: "Enter a first name...",
      options: [],
      component: InputComponent,
    });

    items.push({
      name: "surname",
      label: "Last Name",
      placeholder: "Enter a last name...",
      options: [],
      component: InputComponent,
    });
  }

  if (hasPhoneNumber) {
    items.push({
      name: "phoneNumber",
      label: "Contact Number",
      placeholder: "Enter phone number...",
      component: InputComponent,
    });
  }

  if (hasDateOfBirth) {
    items.push({
      name: "dateOfBirth",
      dateFormat: "YYYY-MM-DD",
      startMode: "year",
      maxDate: getCurrentDateTime(),
      initialDate: "1992-01-01",
      label: "Date Of Birth",
      placeholder: "Select a date of birth...",
      readOnly: true,
      component: DateComponent,
    });
  }

  if (hasGender) {
    items.push({
      name: "gender",
      label: "Gender",
      placeholder: "Select gender...",
      options: genders,
      component: SelectComponent,
    });
  }

  if (hasAgeRange) {
    items.push({
      name: "ageRange",
      label: "Age Range",
      placeholder: "Select age range...",
      options: ageRanges,
      component: SelectComponent,
    });
  }

  if (hasHeight) {
    items.push({
      name: "height",
      label: "Height",
      placeholder: "Select height...",
      options: heights,
      component: SelectComponent,
    });
  }

  if (hasBuild) {
    items.push({
      name: "build",
      label: "Build",
      placeholder: "Select build...",
      options: builds,
      component: SelectComponent,
    });
  }

  if (hasHair) {
    items.push({
      name: "hair",
      label: "Hair Colour",
      placeholder: "Select hair colour...",
      options: hairs,
      component: SelectComponent,
    });
  }

  if (hasEthnicity) {
    items.push({
      name: "ethnicity",
      label: "Ethinicity",
      placeholder: "Select ethnicity...",
      options: ethnicities,
      component: SelectComponent,
    });
  }

  if (hasEmailAddress) {
    items.push({
      name: "emailAddress",
      label: "Email",
      placeholder: "Enter email address...",
      component: InputComponent,
    });
  }

  items.push({
    name: "features",
    label: "Features",
    placeholder: "Please enter any distinguishing features...",
    component: TextareaComponent,
  });

  return items;
};
